/**
 * View As (see @/lib/view-as): an ADMIN starts and leaves a read-only session as another
 * user. Both calls go straight to Better Auth's admin plugin — the server logs the start
 * from the session-create hook and the end from the /api/auth gate.
 *
 * Both mutations finish with a full page load rather than a cache invalidation. Switching
 * identity changes every query, the sidebar, the route guards and the session atom at
 * once; a reload is the only way to guarantee nothing from the previous identity lingers
 * on screen. The banner (or the admin's own screen) is the success feedback, so neither
 * shows a success toast that the reload would wipe.
 */

import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient, useSession } from "@/lib/auth-client";

/** Where the ADMIN lands on each side of View As. */
const VIEW_AS_LANDING = "/dashboard";
const VIEW_AS_EXIT_LANDING = "/dashboard/settings/users";

/** The View As state of the current session: null when not viewing as anyone. */
export function useViewAsState() {
  const { data } = useSession();
  const impersonatedBy = data?.session.impersonatedBy;
  if (!data || !impersonatedBy) return null;
  return { user: data.user, impersonatedBy, expiresAt: new Date(data.session.expiresAt) };
}

export function useStartViewAs() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.impersonateUser({ userId });
      if (error) throw new Error(error.message ?? "Could not start View As");
    },
    onSuccess: () => window.location.assign(VIEW_AS_LANDING),
    onError: (error) => toast.error(error.message || "Could not start View As"),
  });
}

export function useStopViewAs() {
  return useMutation({
    mutationFn: async () => {
      const { error } = await authClient.admin.stopImpersonating();
      if (error) throw new Error(error.message ?? "Could not exit View As");
    },
    onSuccess: () => window.location.assign(VIEW_AS_EXIT_LANDING),
    // Exit can only fail if the ADMIN's own session is gone (expired, or the cookie was
    // cleared). Never leave them stranded as someone else: sign out and start clean.
    onError: async (error) => {
      toast.error(`${error.message || "Could not exit View As"} — signing you out.`);
      await authClient.signOut();
      window.location.assign("/login");
    },
  });
}
